import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { CurrentContextService } from '../../core/context/current-context.service';
import { CatalogOffering, CatalogService, CatalogWarehouse } from './catalog.service';
import { InventoryCostingApiService, InventoryExpiryStatusLine } from './inventory-costing-api.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-expiry-status',
  imports: [
    AinPageComponent, AinStateComponent, InventoryPageHintComponent,
    FormsModule, SelectModule, TableModule, ButtonModule, InputNumberModule
  ],
  template: `
    <ain-page [title]="t('catalog.expiry.title')" [description]="t('catalog.expiry.description')">
      <ain-inventory-page-hint pageKey="expiryStatus" />
      <span class="live-status" [class]="liveUpdateService.connectionState()">{{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}</span>

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="filters">
          <label>
            <span>{{ t('catalog.inventory.warehouseId') }}</span>
            <p-select [(ngModel)]="warehouseFilter" [options]="warehouseOptions()" optionLabel="label" optionValue="value" [showClear]="true" [filter]="true" appendTo="body" (ngModelChange)="load()" />
          </label>
          <label>
            <span>{{ t('catalog.inventory.offeringId') }}</span>
            <p-select [(ngModel)]="offeringFilter" [options]="offeringOptions()" optionLabel="label" optionValue="value" [showClear]="true" [filter]="true" appendTo="body" (ngModelChange)="load()" />
          </label>
          <label>
            <span>{{ t('catalog.expiry.category') }}</span>
            <p-select [(ngModel)]="categoryFilter" [options]="categoryOptions()" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" (ngModelChange)="load()" />
          </label>
          <label>
            <span>{{ t('catalog.expiry.nearExpiryDays') }}</span>
            <p-inputNumber [(ngModel)]="nearExpiryDays" [min]="1" [max]="365" (ngModelChange)="load()" />
          </label>
          <button pButton type="button" severity="secondary" [label]="t('catalog.expiry.exportCsv')" (click)="exportCsv()"></button>
        </section>

        <section class="summary">
          <span class="badge expired">{{ t('catalog.expiry.expiredCount') }}: {{ summary().expiredCount }}</span>
          <span class="badge near">{{ t('catalog.expiry.nearExpiryCount') }}: {{ summary().nearExpiryCount }}</span>
          <span class="badge blocked">{{ t('catalog.expiry.blockedCount') }}: {{ summary().blockedCount }}</span>
        </section>

        <p-table [value]="lines()" [paginator]="true" [rows]="25">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('catalog.expiry.category') }}</th>
              <th>{{ t('catalog.tracking.lotNumber') }}</th>
              <th>{{ t('catalog.inventory.offeringId') }}</th>
              <th>{{ t('catalog.inventory.warehouseId') }}</th>
              <th>{{ t('catalog.inventory.quantity') }}</th>
              <th>{{ t('catalog.expiry.expiryDate') }}</th>
              <th>{{ t('columns.status') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [class.row-expired]="row.category === 'Expired'" [class.row-near]="row.category === 'NearExpiry'">
              <td><span class="cat-badge" [class]="row.category">{{ categoryLabel(row.category) }}</span></td>
              <td>{{ row.lotNumber }}</td>
              <td>{{ offeringLabel(row.offeringId) }}</td>
              <td>{{ warehouseLabel(row.warehouseId) }}</td>
              <td>{{ row.quantity }}</td>
              <td>{{ row.expiryDate || '-' }}</td>
              <td>{{ row.lotStatus }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (error()) { <p class="error">{{ error() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .filters { display: flex; flex-wrap: wrap; gap: 12px; align-items: end; margin-bottom: 12px; }
    .filters label { display: grid; gap: 6px; min-width: 180px; }
    .summary { display: flex; gap: 10px; margin-bottom: 12px; flex-wrap: wrap; }
    .badge { padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; }
    .badge.expired { background: #fee2e2; color: #991b1b; }
    .badge.near { background: #fef3c7; color: #92400e; }
    .badge.blocked { background: #e2e8f0; color: #334155; }
    .cat-badge { padding: 2px 8px; border-radius: 6px; font-size: 11px; font-weight: 600; }
    .cat-badge.Expired { background: #fee2e2; color: #991b1b; }
    .cat-badge.NearExpiry { background: #fef3c7; color: #92400e; }
    .cat-badge.Blocked { background: #e2e8f0; color: #475569; }
    .row-expired { background: #fef2f2; }
    .row-near { background: #fffbeb; }
    .error { color: #b91c1c; }
    .live-status { font-size: 0.8rem; color: #6b7280; display: block; margin-bottom: 8px; }
    .live-status.connected { color: #166534; }
  `]
})
export class InventoryExpiryStatusComponent implements OnInit {
  private readonly costingApi = inject(InventoryCostingApiService);
  private readonly catalog = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly error = signal('');
  readonly lines = signal<InventoryExpiryStatusLine[]>([]);
  readonly summary = signal({ expiredCount: 0, nearExpiryCount: 0, blockedCount: 0 });
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);

  warehouseFilter: string | null = null;
  offeringFilter: string | null = null;
  categoryFilter: string | null = null;
  nearExpiryDays = 30;

  categoryOptions = () => [
    { label: this.t('catalog.expiry.categories.Expired'), value: 'Expired' },
    { label: this.t('catalog.expiry.categories.NearExpiry'), value: 'NearExpiry' },
    { label: this.t('catalog.expiry.categories.Blocked'), value: 'Blocked' },
    { label: this.t('catalog.expiry.categories.Active'), value: 'Active' }
  ];

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => void this.load(false)
    );
    this.catalog.listOfferings().subscribe({ next: o => this.offerings.set(o.filter(x => x.inventoryEnabled)) });
    this.catalog.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    void this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.error.set('');
    this.costingApi.getExpiryStatusReport({
      warehouseId: this.warehouseFilter ?? undefined,
      offeringId: this.offeringFilter ?? undefined,
      nearExpiryDays: this.nearExpiryDays,
      category: this.categoryFilter ?? undefined
    }).subscribe({
      next: report => {
        this.lines.set(report.lines);
        this.summary.set({
          expiredCount: report.expiredCount,
          nearExpiryCount: report.nearExpiryCount,
          blockedCount: report.blockedCount
        });
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('catalog.expiry.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  exportCsv(): void {
    const header = ['Category', 'LotNumber', 'Offering', 'Warehouse', 'Quantity', 'ExpiryDate', 'Status'];
    const rows = this.lines().map(row => [
      row.category,
      row.lotNumber,
      this.offeringLabel(row.offeringId),
      this.warehouseLabel(row.warehouseId),
      String(row.quantity),
      row.expiryDate ?? '',
      row.lotStatus
    ]);
    const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expiry-status-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  offeringOptions = () => this.offerings().map(o => ({ label: `${o.code} — ${o.title}`, value: o.id }));
  warehouseOptions = () => this.warehouses().map(w => ({ label: `${w.code} — ${w.name}`, value: w.id }));
  offeringLabel = (id: string) => {
    const o = this.offerings().find(x => x.id === id);
    return o ? `${o.code} — ${o.title}` : id;
  };
  warehouseLabel = (id: string) => {
    const w = this.warehouses().find(x => x.id === id);
    return w ? `${w.code} — ${w.name}` : id;
  };
  categoryLabel = (cat: string) => {
    const key = `catalog.expiry.categories.${cat}`;
    const translated = this.t(key);
    return translated === key ? cat : translated;
  };

  t(key: string): string { return this.localization.translate(key); }
}
